<template>
  <b-modal
    id="view-detail-modal"
    title="Building View"
    size="xl"
    centered
    hide-footer
    scrollable
    @shown="onModalShown"
    @hidden="onModalHidden"
  >
    <div class="svg-container">
        <svg
          id="buildingSvg"
          ref="svg"
          viewBox="0 0 1800 2100"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <!-- Gradients -->
            <linearGradient id="glassGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stop-color="#3a5670"/>
              <stop offset="30%" stop-color="#2a4a66"/>
              <stop offset="70%" stop-color="#1e3a52"/>
              <stop offset="100%" stop-color="#152a3a"/>
            </linearGradient>
            <linearGradient id="windowGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stop-color="#1e4055"/>
              <stop offset="100%" stop-color="#0f2533"/>
            </linearGradient>
            <linearGradient id="highlightGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#00ddff" stop-opacity="0.4"/>
              <stop offset="50%" stop-color="#00ddff" stop-opacity="0.15"/>
              <stop offset="100%" stop-color="#00ddff" stop-opacity="0"/>
            </linearGradient>
            <linearGradient id="entranceGlow" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stop-color="#00eeff" stop-opacity="0.7"/>
              <stop offset="80%" stop-color="#0099cc" stop-opacity="0.2"/>
              <stop offset="100%" stop-color="#006688" stop-opacity="0"/>
            </linearGradient>

            <!-- Filters -->
            <filter id="labelShadow">
              <feDropShadow dx="0" dy="4" stdDeviation="6" flood-color="#000000" flood-opacity="0.6"/>
            </filter>
          </defs>

          <!-- Twinkling stars -->
          <g id="stars" opacity="0.6">
            <circle cx="200" cy="150" r="1.5" fill="#ffffff">
              <animate attributeName="opacity" values="0.4;0.8;0.4" dur="4s" repeatCount="indefinite"/>
            </circle>
            <circle cx="1600" cy="100" r="1.2" fill="#a0c4ff">
              <animate attributeName="opacity" values="0.4;0.7;0.4" dur="5s" repeatCount="indefinite"/>
            </circle>
          </g>

          <!-- Building Group (tower only) -->
          <g id="building" transform="translate(600,300)  scale(1.5)">
            <!-- Base -->
            <rect x="-50" y="950" width="700" height="150" fill="#15202a" rx="25">
              <animate attributeName="opacity" values="0;1" dur="2s" fill="freeze"/>
            </rect>

            <!-- Main tower -->
            <g id="tower">
              <rect x="0" y="0" width="600" height="1100" fill="url(#glassGrad)" opacity="0.95" stroke="#0a1620" stroke-width="4">
                <animate attributeName="height" values="0;1100" dur="1s" fill="freeze"/>
                <animateTransform attributeName="transform" type="translate" additive="true" values="0,1100;0,0" dur="1" fill="freeze"/>
              </rect>

              <!-- Windows -->
              <g opacity="0.85">
                <!-- Dynamic windows based on actual floor count -->
                <g v-for="(floor, index) in calculatedFloorPositions" :key="'windows-' + index">
                  <!-- Calculate Y position for this floor's windows -->
                  <g :transform="`translate(0, ${floor.windowY})`">
                    <!-- Five window columns for this floor -->
                    <rect x="50"  y="10" width="90" height="80" fill="url(#windowGrad)" rx="8"/>
                    <rect x="160" y="10" width="90" height="80" fill="url(#windowGrad)" rx="8"/>
                    <rect x="270" y="10" width="90" height="80" fill="url(#windowGrad)" rx="8"/>
                    <rect x="380" y="10" width="90" height="80" fill="url(#windowGrad)" rx="8"/>

                    <!-- Optional: glowing highlights on some windows randomly -->
                    <rect x="50"  y="10" width="90" height="80" fill="#00ddff" opacity="0.15" rx="8"
                          v-if="Math.random() > 0.7"/>
                    <rect x="160" y="10" width="90" height="80" fill="#00ddff" opacity="0.15" rx="8"
                          v-if="Math.random() > 0.7"/>
                    <rect x="380" y="10" width="90" height="80" fill="#00ddff" opacity="0.15" rx="8"
                          v-if="Math.random() > 0.7"/>
                  </g>
                </g>

                <!-- Keep the horizontal floor separator lines (optional but recommended) -->
                <g stroke="#00ddff" stroke-width="1.5" opacity="0.25">
                  <line v-for="(floor, index) in calculatedFloorPositions" 
                        :key="'separator-' + index"
                        x1="0" :y1="floor.separatorY" x2="600" :y2="floor.separatorY">
                    <animate attributeName="opacity" values="0.2;0.4;0.2" 
                            :begin="index * 0.8 + 's'" dur="6s" repeatCount="indefinite"/>
                  </line>
                </g>

                <!-- Highlight overlay -->
                <rect x="0" y="0" width="600" height="1100" fill="url(#highlightGrad)" opacity="0.2"/>
              </g>

              <!-- Side depth -->
              <polygon points="0,0 35,60 35,1040 0,1100" fill="#0f1f2f" opacity="0.8"/>
              <polygon points="600,0 565,60 565,1040 600,1100" fill="#0f1f2f" opacity="0.8"/>

              <!-- Roof -->
              <g id="roof">
                <rect x="-40" y="-80" width="680" height="100" fill="#1e2a38" rx="20"/>
                <rect x="20" y="-60" width="560" height="40" fill="#15202c" rx="10"/>
                <rect x="100" y="-110" width="120" height="50" fill="#0f1822" rx="8"/>
                <rect x="380" y="-110" width="120" height="50" fill="#0f1822" rx="8"/>
                <rect x="240" y="-100" width="120" height="40" fill="#0f1822" rx="6"/>
              </g>
            </g>
          </g>

          <!-- Entrance (in front) -->
          <g id="entrance" transform="translate(600,300)  scale(1.5)">
            <rect x="200" y="920" width="200" height="180" fill="#0a1218" rx="15">
              <animate attributeName="opacity" values="0;1" dur="2.5s" fill="freeze"/>
            </rect>

            <g id="leftDoor">
              <rect x="220" y="940" width="80" height="140" fill="#1e4055" rx="8" opacity="0.8"/>
              <rect x="220" y="940" width="80" height="140" fill="none" stroke="#00ddff" stroke-width="3" opacity="0.6"/>
              <animateTransform attributeName="transform" type="translate" values="0,0; -80,0; -80,0; 0,0" dur="8s" repeatCount="indefinite"/>
            </g>

            <g id="rightDoor">
              <rect x="300" y="940" width="80" height="140" fill="#1e4055" rx="8" opacity="0.8"/>
              <rect x="300" y="940" width="80" height="140" fill="none" stroke="#00ddff" stroke-width="3" opacity="0.6"/>
              <animateTransform attributeName="transform" type="translate" values="0,0; 80,0; 80,0; 0,0" dur="8s" repeatCount="indefinite"/>
            </g>

            <rect x="220" y="940" width="160" height="140" fill="url(#entranceGlow)" opacity="0.5">
              <animate attributeName="opacity" values="0.4;0.8;0.8;0.4" dur="8s" repeatCount="indefinite"/>
            </rect>

            <rect x="180" y="900" width="240" height="40" fill="#1e2a38" rx="12">
              <animate attributeName="opacity" values="0;1" dur="3s" fill="freeze"/>
            </rect>
            <rect x="190" y="910" width="220" height="20" fill="#0f1822"/>
          </g>

          <!-- Title -->
          <text x="100" y="180" fill="#e0e0e0" font-size="68" font-weight="bold" opacity="0">
            <animate attributeName="opacity" values="0;1" dur="2s" fill="freeze"/>
          </text>Building-view</text>
          <text x="100" y="250" fill="#88aaaa" font-size="38" opacity="0">
            <animate attributeName="opacity" values="0;1" begin="2.5s" dur="1.5s" fill="freeze"/>
          </text>Architectural Safety View</text>

          <!-- Subtle particles -->
          <g opacity="0.5">
            <circle cx="700" cy="600" r="2.5" fill="#00ddff">
              <animate attributeName="cy" values="600;300" dur="12s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0;0.8;0" dur="12s" repeatCount="indefinite"/>
            </circle>
            <circle cx="1100" cy="800" r="2" fill="#4caf50">
              <animate attributeName="cy" values="800;500" dur="14s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0;0.8;0" dur="14s" repeatCount="indefinite"/>
            </circle>
          </g>

          <!-- Dynamic Floor Cards Group -->
          <g id="floorCards"></g>
        </svg>
      </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  props: {
    building: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      // Default floor data (can be overridden by API or props later)
      floorData: {        
      },
      is_peep : "",
      in_building : "",
      reached_assembly_area : "",
      floorData: {},
      buildingEmergencyModalCount: 0,
      floor: {},
    };
  },
  methods: {
    ...mapActions({
      getBuildingFloorStatus: "appData/getBuildingFloorStatus",
    }),
    async onModalShown() {
      const building_data = await this.getBuildingFloorStatus({pk: this.building.id});
      this.floorData = building_data.data;
      this.renderFloors();
    },
    onModalHidden() {
      // Optional: cleanup if needed
    },
    
    calculatePositions() {
      const totalFloors = this.floorData.floors.length;
      const startY = 250;
      const endY = 1800;
      const spacing = (endY - startY) / (totalFloors - 1);

      return this.floorData.floors.slice().reverse().map((floor, index) => {
        const cardY = startY + index * spacing;
        const lineY = cardY + 90;
        const isRightSide = index % 2 === 0;
        return { ...floor, cardY, lineY, isRightSide };
      });
    },
    renderFloors() {
      const svg = this.$refs.svg;
      if (!svg) return;

      const floorCardsGroup = svg.querySelector('#floorCards');
      if (!floorCardsGroup) return;

      const positions = this.calculatePositions();

      /////////////////////////////////////////////
        // Clear previous content
      floorCardsGroup.innerHTML = '';

      // === FULL-WIDTH WINDOW PER FLOOR - EQUAL FLOOR HEIGHTS ===
      const tower = svg.querySelector('#tower');
      const windowsParent = tower.querySelector('g[opacity="0.85"]');
      if (windowsParent) {
        // Keep only the highlight overlay
        windowsParent.innerHTML = '<rect x="0" y="0" width="600" height="1100" fill="url(#highlightGrad)" opacity="0.2"/>';
      } else {
        return;
      }

      const floorCount = this.floorData.floors ? this.floorData.floors.length : 10;
      if (floorCount === 0) return;

      // Use the FULL tower height (from y=0 to y=1100 inside the tower rect)
      const totalTowerHeight = 1100;
      const floorHeight = totalTowerHeight / floorCount;

      // Window takes ~60% of floor height, centered vertically
      const windowHeight = floorHeight * 0.80;
      const verticalPadding = (floorHeight - windowHeight) / 2;

      // Reverse floors to match visual order (top floor first)
      const reversedFloors = this.floorData.floors.slice().reverse();

      // Render one full-width window bar per floor
      for (let i = 0; i < floorCount; i++) {
        const yBase = i * floorHeight + verticalPadding;

        const windowGroup = document.createElementNS("http://www.w3.org/2000/svg", 'g');
        windowGroup.setAttribute('transform', `translate(0, ${yBase})`);

        // Main full-width dark window bar
        const win = document.createElementNS("http://www.w3.org/2000/svg", 'rect');
        win.setAttribute('x', 0);
        win.setAttribute('y', 0);
        win.setAttribute('width', 600);
        win.setAttribute('height', windowHeight);
        win.setAttribute('rx', 8);
        win.setAttribute('fill', 'url(#windowGrad)');
        windowGroup.appendChild(win);

        // Glow for floors with occupants
        const floor = reversedFloors[i];
        const safePeople = Number(floor.safe) === Number(floor.visitors) + Number(floor.residents) + Number(floor.peeps);

        if (Number(floor.peeps) > 0) {
          const glow = document.createElementNS("http://www.w3.org/2000/svg", 'rect');
          glow.setAttribute('x', 0);
          glow.setAttribute('y', 0);
          glow.setAttribute('width', 600);
          glow.setAttribute('height', windowHeight);
          glow.setAttribute('rx', 8);
          glow.setAttribute('fill', '#ffbf00');
          glow.setAttribute('opacity', '0.35');

          const animate = document.createElementNS("http://www.w3.org/2000/svg", 'animate');
          animate.setAttribute('attributeName', 'opacity');
          animate.setAttribute('values', '0.25;0.5;0.25');
          animate.setAttribute('dur', '1s');
          animate.setAttribute('repeatCount', 'indefinite');
          glow.appendChild(animate);

          windowGroup.appendChild(glow);
        }

        windowsParent.insertBefore(windowGroup, windowsParent.firstChild);
      }

      // Subtle glowing floor dividers
      const separators = document.createElementNS("http://www.w3.org/2000/svg", 'g');
      separators.setAttribute('stroke', '#00ddff');
      separators.setAttribute('stroke-width', '1.5');
      separators.setAttribute('opacity', '0.25');

      for (let i = 1; i < floorCount; i++) {
        const lineY = i * floorHeight;
        const line = document.createElementNS("http://www.w3.org/2000/svg", 'line');
        line.setAttribute('x1', 0);
        line.setAttribute('y1', lineY);
        line.setAttribute('x2', 600);
        line.setAttribute('y2', lineY);

        const anim = document.createElementNS("http://www.w3.org/2000/svg", 'animate');
        anim.setAttribute('attributeName', 'opacity');
        anim.setAttribute('values', '0.2;0.4;0.2');
        anim.setAttribute('begin', `${i * 0.5}s`);
        anim.setAttribute('dur', '1s');
        anim.setAttribute('repeatCount', 'indefinite');
        line.appendChild(anim);

        separators.appendChild(line);
      }
      windowsParent.insertBefore(separators, windowsParent.firstChild);
      // === END EQUAL FLOOR HEIGHTS ===

      ////////////////////////////////////////////

      // Lines group (first)
      const linesGroup = document.createElementNS("http://www.w3.org/2000/svg", 'g');
      floorCardsGroup.appendChild(linesGroup);

      // Cards group (after lines)
      const cardsGroup = document.createElementNS("http://www.w3.org/2000/svg", 'g');
      cardsGroup.setAttribute('font-family', "'Segoe UI', Arial, sans-serif");
      floorCardsGroup.appendChild(cardsGroup);

      positions.forEach((floor, index) => {
        const id = `label${index + 1}`;

        // Building center X
        const buildingCenterX = 1100;

        const lineStartX = floor.isRightSide ? buildingCenterX + 50 : buildingCenterX - 50;
        const lineEndX = floor.isRightSide ? 1700 : 100;

        // Line
        const line = document.createElementNS("http://www.w3.org/2000/svg", 'line');
        line.setAttribute('x1', lineStartX);
        line.setAttribute('y1', floor.lineY);
        line.setAttribute('x2', lineEndX);
        line.setAttribute('y2', floor.lineY);
        line.setAttribute('stroke', '#666');
        line.setAttribute('stroke-width', '3');
        line.setAttribute('stroke-dasharray', '8,6');

        const lineAnim = document.createElementNS("http://www.w3.org/2000/svg", 'animate');
        lineAnim.setAttribute('attributeName', 'stroke-dashoffset');
        lineAnim.setAttribute('values', '0;14;0');
        lineAnim.setAttribute('dur', '1s');
        lineAnim.setAttribute('repeatCount', 'indefinite');
        line.appendChild(lineAnim);
        linesGroup.appendChild(line);

        // Card
        const cardX = floor.isRightSide ? 1700 : -200;
        const cardWidth = 600;

        const g = document.createElementNS("http://www.w3.org/2000/svg", 'g');


        g.style.cursor = 'pointer';
        g.style.transition = 'transform 0.4s ease';
        g.style.transformBox = 'fill-box';
        g.style.transformOrigin = 'center center';

        g.addEventListener('mouseenter', () => {
          g.style.transform = 'scale(2)';
          g.parentNode.appendChild(g);
        });

        g.addEventListener('mouseleave', () => {
          g.style.transform = 'scale(1)';
        });

        g.addEventListener('click', () => {
          this.floor = { ...floor };
          this.viewBuildingEmergency();
        });


        g.setAttribute('id', id);
        cardsGroup.appendChild(g);

        let background_color = '';

        if (
          Number(floor.peeps) > 0
        ) {
          background_color = '#ffbf00';
        }
        else
        {
          background_color = '#229400';
        }
        
        // Background
        const rect = document.createElementNS("http://www.w3.org/2000/svg", 'rect');
        rect.setAttribute('x', cardX);
        rect.setAttribute('y', floor.cardY);
        rect.setAttribute('width', cardWidth);
        rect.setAttribute('height', 180);
        rect.setAttribute('rx', 18);
        rect.setAttribute('fill', `${background_color}`);
        rect.setAttribute('stroke', 'black');
        rect.setAttribute('stroke-width', 2);
        rect.setAttribute('opacity', 0);
        rect.setAttribute('filter', 'url(#labelShadow)');
        g.appendChild(rect);

        // Texts
        this.addText(g, cardX + 30, floor.cardY + 50, 'white', 30, floor.floor, 0);
        this.addText(g, cardX + 30, floor.cardY + 100, 'white', 36, `Residents: ${floor.residents.toLocaleString()}`, 0, 'bold');
        this.addText(g, cardX + 30, floor.cardY + 150, 'white', 30, `PEEPs: ${floor.peeps.toLocaleString()}`, 0);
        this.addText(g, cardX + 200, floor.cardY + 150, 'white', 30, `Visitors: ${floor.visitors}`, 0);
        
      });

      // Sequential fade-in
      positions.forEach((floor, index) => {
        setTimeout(() => {
          const group = svg.getElementById(`label${index + 1}`);
          if (group) {
            group.querySelectorAll('rect, text').forEach(el => {
              el.setAttribute('opacity', '1');
            });
          }
        }, 1000 + index * 100);
      });
    },
    addText(parent, x, y, fill, fontSize, text, opacity, weight = 'normal') {
      const textEl = document.createElementNS("http://www.w3.org/2000/svg", 'text');
      textEl.setAttribute('x', x);
      textEl.setAttribute('y', y);
      textEl.setAttribute('fill', fill);
      textEl.setAttribute('font-size', fontSize);
      if (weight === 'bold') textEl.setAttribute('font-weight', 'bold');
      textEl.setAttribute('opacity', opacity);
      textEl.textContent = text;
      parent.appendChild(textEl);
    }
  }
};
</script>

<style scoped>
.amber-background {
  background-color: #ffbf00 !important;
}
.svg-container {
  width: 100%;
  height: 75vh;
  overflow: hidden;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
}

#buildingSvg {
  width: 100%;
  height: 100%;
  max-height: 100vh;
}
.amber-background {
  background-color: #ffbf00 !important;
}
</style>